import React, { useState, useEffect, useMemo } from 'react';
import { Spinner, Alert, Form, Button } from 'react-bootstrap';
import { ResponsiveSankey } from '@nivo/sankey';
import { fetchCaseRelationships } from '../../api/cases';
import { useAuth } from '../../contexts/AuthContext';
import { CaseEntity, EntityRelationship } from '../../types';
import { formatCurrency } from '../../utils/formatters';

interface Props {
  case_id: string;
}

const TYPE_COLOR: Record<string, string> = {
  person:       '#0d6efd',
  business:     '#198754',
  bank_account: '#e6a817',
  gl_account:   '#0dcaf0',
  vendor:       '#6c757d',
  other:        '#343a40',
};

const TYPE_LABEL: Record<string, string> = {
  person:       'Person',
  business:     'Business',
  bank_account: 'Bank Account',
  gl_account:   'GL Account',
  vendor:       'Vendor',
  other:        'Other',
};

interface AggEdge {
  from: string;
  to: string;
  totalAmount: number;
  count: number;
}

function aggregateRelationships(rels: EntityRelationship[]): AggEdge[] {
  const map = new Map<string, AggEdge>();
  for (const r of rels) {
    const from = r.from_entity_id;
    const to   = r.to_entity_id;
    if (!from || !to || from === to) continue;
    const key = `${from}→${to}`;
    const existing = map.get(key);
    const amt = r.amount ?? 0;
    if (existing) {
      existing.totalAmount += amt;
      existing.count       += 1;
    } else {
      map.set(key, { from, to, totalAmount: amt, count: 1 });
    }
  }
  return Array.from(map.values());
}

/**
 * Sankey charts can't have cycles (A→B and B→A). Collapse bidirectional pairs
 * into a single net-flow edge in the dominant direction.
 */
function resolveNetFlows(edges: AggEdge[]): AggEdge[] {
  const seen = new Set<string>();
  const out: AggEdge[] = [];
  for (const e of edges) {
    const fwd = `${e.from}→${e.to}`;
    const rev = `${e.to}→${e.from}`;
    if (seen.has(fwd) || seen.has(rev)) continue;
    seen.add(fwd);
    seen.add(rev);
    const opp = edges.find(r => r.from === e.to && r.to === e.from);
    if (!opp) {
      out.push(e);
    } else {
      const net = Math.abs(e.totalAmount) - Math.abs(opp.totalAmount);
      if (Math.abs(net) > 0.01) {
        out.push(net > 0 ? { ...e, totalAmount: net } : { ...opp, totalAmount: -net });
      }
    }
  }
  return out;
}

/**
 * resolveNetFlows only collapses direct A↔B pairs. Longer cycles (A→B→C→A)
 * still reach nivo/d3-sankey and throw "circular link", crashing the whole
 * page. Greedily accept edges strongest-first, skipping any edge that would
 * close a cycle given the edges already accepted - this guarantees a DAG.
 */
function breakCycles(edges: AggEdge[]): AggEdge[] {
  const sorted = [...edges].sort((a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount));
  const adjacency = new Map<string, Set<string>>();
  const accepted: AggEdge[] = [];

  const hasPath = (start: string, end: string): boolean => {
    const visited = new Set<string>();
    const stack = [start];
    while (stack.length) {
      const node = stack.pop()!;
      if (node === end) return true;
      if (visited.has(node)) continue;
      visited.add(node);
      adjacency.get(node)?.forEach(n => stack.push(n));
    }
    return false;
  };

  for (const e of sorted) {
    if (hasPath(e.to, e.from)) continue; // accepting e would close a cycle
    accepted.push(e);
    if (!adjacency.has(e.from)) adjacency.set(e.from, new Set());
    adjacency.get(e.from)!.add(e.to);
  }
  return accepted;
}

const EntityGraph: React.FC<Props> = ({ case_id }) => {
  const auth  = useAuth();
  const token = auth.user?.id_token;

  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [rawRels, setRawRels]       = useState<EntityRelationship[]>([]);
  const [entityList, setEntityList] = useState<CaseEntity[]>([]);
  const [minAmount, setMinAmount]   = useState(0);

  useEffect(() => {
    setLoading(true);
    fetchCaseRelationships(case_id, token)
      .then(({ relationships, entities }) => {
        setRawRels(relationships);
        setEntityList(entities);
      })
      .catch((e) => setError(e?.message ?? 'Failed to load relationships'))
      .finally(() => setLoading(false));
  }, [case_id, token]);

  const entityById = useMemo(
    () => new Map(entityList.map(e => [e.entity_id, e])),
    [entityList],
  );

  const aggEdges  = useMemo(() => aggregateRelationships(rawRels), [rawRels]);
  const maxAmount = useMemo(
    () => Math.max(...aggEdges.map(a => Math.abs(a.totalAmount)), 0),
    [aggEdges],
  );

  const sankeyData = useMemo(() => {
    const filtered = breakCycles(
      resolveNetFlows(aggEdges.filter(a => Math.abs(a.totalAmount) >= minAmount)),
    );
    if (filtered.length === 0) return { nodes: [] as { id: string }[], links: [] as { source: string; target: string; value: number }[] };
    const nodeIds = new Set<string>();
    filtered.forEach(e => { nodeIds.add(e.from); nodeIds.add(e.to); });
    return {
      nodes: Array.from(nodeIds).map(id => ({ id })),
      links: filtered.map(e => ({
        source: e.from,
        target: e.to,
        value:  Math.max(Math.abs(e.totalAmount), 1),
      })),
    };
  }, [aggEdges, minAmount]);

  if (loading) return (
    <div className="text-center py-5">
      <Spinner animation="border" />
      <p className="mt-2 text-muted">Loading money flow graph…</p>
    </div>
  );

  if (error) return <Alert variant="danger">{error}</Alert>;

  if (rawRels.length === 0) return (
    <Alert variant="info">
      No entity relationships found for this case yet. Process bank statements to generate money flow data.
    </Alert>
  );

  const unresolvedCount = rawRels.filter(r => !r.from_entity_id || !r.to_entity_id).length;
  const step = Math.max(100, Math.floor(maxAmount / 200));

  return (
    <div>
      {/* Controls */}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: 420 }}>
          <span className="text-muted text-nowrap" style={{ fontSize: 13 }}>Min amount:</span>
          <Form.Range
            min={0}
            max={Math.floor(maxAmount)}
            step={step}
            value={minAmount}
            onChange={(e) => setMinAmount(Number(e.target.value))}
          />
          <span className="fw-semibold text-nowrap" style={{ fontSize: 13, minWidth: 90 }}>
            {formatCurrency(minAmount)}
          </span>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => setMinAmount(0)}>
          Reset
        </Button>
        <span className="text-muted" style={{ fontSize: 12 }}>
          {sankeyData.links.length} flows · {sankeyData.nodes.length} entities
          {unresolvedCount > 0 && (
            <span className="text-warning"> · {unresolvedCount} unresolved</span>
          )}
        </span>
      </div>

      {/* Legend */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {Object.entries(TYPE_COLOR).map(([type, color]) => (
          <span
            key={type}
            className="badge"
            style={{ background: color, color: type === 'bank_account' ? '#000' : '#fff', fontSize: 11 }}
          >
            {TYPE_LABEL[type]}
          </span>
        ))}
      </div>

      {/* Sankey */}
      {sankeyData.nodes.length === 0 ? (
        <Alert variant="warning">No flows match the current filter. Lower the minimum amount.</Alert>
      ) : (
        <div style={{ height: 580 }}>
          <ResponsiveSankey
            data={sankeyData}
            margin={{ top: 10, right: 220, bottom: 10, left: 220 }}
            align="justify"
            colors={(node) =>
              TYPE_COLOR[entityById.get(node.id)?.entity_type ?? 'other'] ?? TYPE_COLOR.other
            }
            nodeOpacity={1}
            nodeHoverOthersOpacity={0.25}
            nodeThickness={22}
            nodeInnerPadding={3}
            nodeSpacing={28}
            nodeBorderWidth={0}
            nodeBorderRadius={4}
            linkOpacity={0.4}
            linkHoverOthersOpacity={0.06}
            linkContract={2}
            enableLinkGradient={true}
            enableLabels={true}
            labelPosition="outside"
            labelOrientation="horizontal"
            labelPadding={16}
            label={(node) => entityById.get(node.id)?.name ?? String(node.id)}
            labelTextColor="#333333"
            nodeTooltip={({ node }) => {
              const entity  = entityById.get(node.id);
              const inFlow  = node.targetLinks.reduce((s, l) => s + l.value, 0);
              const outFlow = node.sourceLinks.reduce((s, l) => s + l.value, 0);
              return (
                <div style={{
                  background: '#fff', padding: '8px 12px', border: '1px solid #dee2e6',
                  borderRadius: 6, fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', maxWidth: 220,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{entity?.name ?? node.id}</div>
                  {entity && (
                    <div style={{ color: '#6c757d', marginBottom: 6 }}>
                      {TYPE_LABEL[entity.entity_type] ?? entity.entity_type}
                    </div>
                  )}
                  {inFlow  > 0 && <div>Received: <strong>{formatCurrency(inFlow)}</strong></div>}
                  {outFlow > 0 && <div>Sent: <strong>{formatCurrency(outFlow)}</strong></div>}
                </div>
              );
            }}
            linkTooltip={({ link }) => {
              const from = entityById.get(link.source.id);
              const to   = entityById.get(link.target.id);
              return (
                <div style={{
                  background: '#fff', padding: '8px 12px', border: '1px solid #dee2e6',
                  borderRadius: 6, fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}>
                  <div style={{ color: '#6c757d', fontSize: 11 }}>From</div>
                  <div style={{ fontWeight: 600 }}>{from?.name ?? link.source.id}</div>
                  <div style={{ color: '#6c757d', fontSize: 11, marginTop: 4 }}>To</div>
                  <div style={{ fontWeight: 600 }}>{to?.name ?? link.target.id}</div>
                  <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700 }}>{formatCurrency(link.value)}</div>
                </div>
              );
            }}
          />
        </div>
      )}
      <p className="text-muted mt-2" style={{ fontSize: 11 }}>
        Band width is proportional to transaction amount. Hover nodes or flows for details.
      </p>
    </div>
  );
};

export default EntityGraph;
